import React, {useContext} from 'react'
import useListScoredScenes from "./useListScoredScenes";
import {useStyles, VideoCutSlider} from "./ScoredScenesSection.css";
import {StyledTableCell, StyledTableRow} from "../../../../commons/utils/table/StyledTableSecondary.css";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import {Delete, Edit, Info} from "@mui/icons-material";
import {CircularProgress, IconButton} from "@mui/material";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import TagContainer from "../../../../commons/utils/disciplines/TagContainer";
import ScoredSceneSummarySlider from "../utils/sliders/ScoredSceneSummarySlider";
import DifficultyIndication from "../../../../commons/utils/difficulty/DifficultyIndication";
import {useIntl} from "react-intl";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import ScoredSceneStatusIndication from "../../../../commons/utils/status/scoredScene/ScoredSceneStatusIndication";

const ScoredScenesSection = ({videoKey, onEdit, onScoredScenesUpdated, editButtonEnabled}) => {
    const classes = useStyles();

    const {
        scoredScenes,
        loading,
        handleDeleteScoredScene,
        deletionAttemptedScoredScene,
        deletionInProgress,
    } = useListScoredScenes(videoKey, onScoredScenesUpdated)

    // Internationalization for simple strings
    const intl = useIntl();

    // Checking if the edit/saving operations are allowed
    const {isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);

    // Show loading indication while loading the list of users
    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.scoredScene.overview.loading',
                defaultMessage: 'Loading Video Scenes...'
            })}/>
    }

    if (deletionInProgress) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.scoredScene.overview.delete.loading',
                defaultMessage: 'Deleting Video Scene...'
            })}/>
    }

    if (scoredScenes?.length === 0) {
        return <div className={classes.firstSceneContainer}>
            <b>
                {intl.formatMessage({
                    id: 'app.scoredScene.overview.noScoresDefinedYet',
                    defaultMessage: 'No scored video scenes defined yet for this video.'
                })}
            </b>
        </div>
    }

    return (
        <Paper className={classes.root}>
            <TableContainer component={Paper}>
                <Table size="small">

                    {/* Table header */}
                    <TableHead>
                        <TableRow>
                            <StyledTableCell align={"center"} width={"10%"} key='status'>
                                {intl.formatMessage({
                                    id: 'app.scoredScene.overview.table.column.status',
                                    defaultMessage: 'Status'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"25%"} key='scoreSummary'>
                                {intl.formatMessage({
                                    id: 'app.scoredScene.overview.table.column.scoreSummary',
                                    defaultMessage: 'Score Summary'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"15%"} key='rangeOverivew'>
                                {intl.formatMessage({
                                    id: 'app.scoredScene.overview.table.column.timeRange',
                                    defaultMessage: 'Time Range'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"10%"} key='difficulty'>
                                {intl.formatMessage({
                                    id: 'app.scoredScene.overview.table.column.difficulty',
                                    defaultMessage: 'Difficulty'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"15%"} key='mainFocus'>
                                {intl.formatMessage({
                                    id: 'app.scoredScene.overview.table.column.mainFocus',
                                    defaultMessage: 'Main Focus'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"10%"} key='athletesStartPosition'>
                                {intl.formatMessage({
                                    id: 'app.scoredScene.overview.table.column.startPosition',
                                    defaultMessage: 'Start Position'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"right"} width={"15%"} key='actions'>
                                {intl.formatMessage({
                                    id: 'app.scoredScene.overview.table.column.actions',
                                    defaultMessage: 'Actions'
                                })}
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>

                    {/* Table rows */}
                    <TableBody>
                        {scoredScenes.map((scoredScene) => {
                            return (
                                <StyledTableRow hover key={scoredScene.key}>

                                    <StyledTableCell key='status'>
                                        <ScoredSceneStatusIndication statusInfo={scoredScene.statusInfo} />
                                    </StyledTableCell>

                                    {/* Score summary "slider" */}
                                    <StyledTableCell key='scoreSummary'>
                                        <div className={classes.scoreSummaryRow}>
                                            <ScoredSceneSummarySlider scoredScene={scoredScene} timeFormat="mm:ss:SS"/>
                                        </div>
                                    </StyledTableCell>
                                    <StyledTableCell key='timeRange'>
                                        <div className={classes.scoreSummaryRow}>
                                            <VideoCutSlider
                                                value={[scoredScene.basics.sceneStart, scoredScene.basics.sceneEnd]}
                                                valueLabelDisplay="off"
                                                min={0}
                                                max={scoredScene.video.vimeo.duration}
                                            />
                                        </div>
                                    </StyledTableCell>
                                    <StyledTableCell key='difficulty'>
                                        <DifficultyIndication difficulty={scoredScene.basics.difficulty}/>
                                    </StyledTableCell>
                                    <StyledTableCell key='mainFocus'>
                                        <TagContainer maxTags={3} tags={scoredScene.basics.mainFocus}/>
                                    </StyledTableCell>
                                    <StyledTableCell key='athletesStartPosition'>
                                        <div className={classes.athletePositionBoxContainer}>
                                            <div
                                                className={scoredScene.basics.athleteLeftPosition === 'red' ? classes.redBox : classes.blueBox}>
                                                {intl.formatMessage({
                                                    id: 'app.ids.abbreviation.athleteStartPosition.left',
                                                    defaultMessage: 'L'
                                                })}
                                            </div>
                                            <div
                                                className={scoredScene.basics.athleteLeftPosition === 'red' ? classes.blueBox : classes.redBox}>
                                                {intl.formatMessage({
                                                    id: 'app.ids.abbreviation.athleteStartPosition.right',
                                                    defaultMessage: 'R'
                                                })}
                                            </div>
                                        </div>
                                    </StyledTableCell>
                                    <StyledTableCell align={"right"} key='action'>
                                        {onEdit && <IconButton
                                            onClick={() => onEdit(scoredScene)}
                                            disabled={!editButtonEnabled}
                                            size="large">
                                            {isOperationOnObjectAllowed(scoredScene)
                                                ? <Edit/>
                                                : <Info/>}
                                        </IconButton>}
                                        <IconButton
                                            onClick={() => handleDeleteScoredScene(scoredScene)}
                                            disabled={!isOperationOnObjectAllowed(scoredScene)}
                                            size="large">
                                            {scoredScene.key === deletionAttemptedScoredScene
                                                ? <CircularProgress style={{height: 20, width: 20}}/>
                                                : <Delete/>}
                                        </IconButton>
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default ScoredScenesSection
